import { useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveImagePath } from '@/utils/imageMap';
import navigationData from '@/data/navigation.json';
import { cn } from '@/utils/cn';

interface NavigationItem {
  label: string;
  href: string;
  isActive?: boolean;
  external?: boolean;
  highlight?: boolean;
  submenu?: NavigationItem[];
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const navigation = navigationData.navigation as NavigationItem[];

  const handleDropdownToggle = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  return (
    <header className="bg-white shadow-sm relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img
                src={resolveImagePath('/images/logo.png')}
                alt="Minute Man Air Field"
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navigation.map((item) => (
              <div key={item.label} className="relative">
                {item.submenu ? (
                  <div className="group">
                    <button
                      className={cn(
                        'flex items-center font-nav text-neutral-dark hover:text-primary transition-colors',
                        item.isActive && 'text-primary font-bold'
                      )}
                      onClick={() => handleDropdownToggle(item.label)}
                    >
                      {item.label}
                      <svg className="ml-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeDropdown === item.label && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white shadow-lg border border-gray-200 rounded-md py-2 z-50">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.label}
                            to={subItem.href}
                            className="block px-4 py-2 text-sm text-neutral-dark hover:bg-gray-50 hover:text-primary transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'font-nav text-neutral-dark hover:text-primary transition-colors',
                      item.highlight && 'bg-accent-red text-white px-3 py-2 rounded-md hover:bg-red-600',
                      item.isActive && 'text-primary font-bold'
                    )}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'font-nav text-neutral-dark hover:text-primary transition-colors',
                      item.highlight && 'bg-accent-red text-white px-3 py-2 rounded-md hover:bg-red-600',
                      item.isActive && 'text-primary font-bold'
                    )}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md text-neutral-dark hover:text-primary hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <nav className="space-y-2">
              {navigation.map((item) => (
                <div key={item.label}>
                  {item.submenu ? (
                    <div>
                      <button
                        className={cn(
                          'w-full text-left px-3 py-2 font-nav text-neutral-dark hover:text-primary transition-colors flex items-center justify-between',
                          item.isActive && 'text-primary font-bold'
                        )}
                        onClick={() => handleDropdownToggle(`mobile-${item.label}`)}
                      >
                        {item.label}
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      
                      {activeDropdown === `mobile-${item.label}` && (
                        <div className="pl-6 space-y-1">
                          {item.submenu.map((subItem) => (
                            <Link
                              key={subItem.label}
                              to={subItem.href}
                              className="block px-3 py-2 text-sm text-neutral-dark hover:text-primary transition-colors"
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setActiveDropdown(null);
                              }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        'block px-3 py-2 font-nav text-neutral-dark hover:text-primary transition-colors',
                        item.highlight && 'bg-accent-red text-white rounded-md hover:bg-red-600',
                        item.isActive && 'text-primary font-bold'
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        'block px-3 py-2 font-nav text-neutral-dark hover:text-primary transition-colors',
                        item.highlight && 'bg-accent-red text-white rounded-md hover:bg-red-600',
                        item.isActive && 'text-primary font-bold'
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}
      </div>

      {/* Backdrop to close dropdowns when clicking outside */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </header>
  );
}